import { useRolodexEdit } from 'features/fde'
import { RolodexDocumentEntry } from 'features/files'
import { useRolodexEntries } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { RolodexEntryType } from 'types'
import { v4 } from 'uuid'

const ROLODEX_DOC_FIELDS_TYPE = 'document-fields' as RolodexEntryType

/**
 * Hook to get document field values for a specific document
 */
export const useDocumentFieldValues = (
  document: RolodexDocumentEntry | null
): {
  fieldValues: Record<string, unknown>
  entryId: string | undefined
  isLoading: boolean
  forceRefresh: () => Promise<void>
} => {
  const { entries, isLoading, forceRefresh } = useRolodexEntries(
    useMemo(
      () =>
        document
          ? {
              type: ROLODEX_DOC_FIELDS_TYPE,
              tags: { [document.type]: document.entry_id },
            }
          : undefined,
      [document]
    )
  )

  const fieldValues = useMemo(() => {
    const entry = entries[0]

    if (!entry?.data?.fields) {
      return {}
    }
    return entry.data.fields as Record<string, unknown>
  }, [entries])

  const entryId = useMemo(() => {
    return entries[0]?.id
  }, [entries])

  return {
    fieldValues,
    entryId,
    isLoading,
    forceRefresh,
  }
}

/**
 * Hook to save document field values for a specific document
 * Returns the created entry ID for subsequent updates
 */
export const useSaveDocumentFieldValues = (): {
  doSave: (
    document: RolodexDocumentEntry,
    unitId: string,
    fieldValues: Record<string, unknown>,
    existingEntryId?: string
  ) => Promise<string>
} => {
  const { rolodexPatch } = useRolodexEdit()

  const doSave = useCallback(
    async (
      document: RolodexDocumentEntry,
      unitId: string,
      fieldValues: Record<string, unknown>,
      existingEntryId?: string
    ): Promise<string> => {
      // Filter out undefined values
      const cleanedFields = Object.entries(fieldValues).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, unknown>
      )

      // Use existing entry ID if provided, otherwise create a new one
      // The format is: fields-{documentId}-{random6chars}
      const entryId =
        existingEntryId ?? `fields-${document.entry_id}-${v4().substring(0, 6)}`

      await rolodexPatch([
        {
          id: entryId,
          type: ROLODEX_DOC_FIELDS_TYPE,
          data: {
            title:
              document.visible_name || document.filename || 'Document Fields',
            fields: cleanedFields,
          },
          tags: {
            [document.type]: document.entry_id,
            unit: unitId,
          },
        },
      ])

      return entryId
    },
    [rolodexPatch]
  )

  return useMemo(() => ({ doSave }), [doSave])
}
